import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type KrUpdateProviderProps = {
  children: ReactNode;
};

type KrUpdateError = {
  field: string;
  updateId: string;
  errorMsg: string;
}

type KrUpdateValidationContext = {
  errors: KrUpdateError[];
  setErrors: (errors: KrUpdateError[], persist?: boolean) => void;
  clearErrors: (errors: Pick<KrUpdateError, 'field' | 'updateId'>[]) => void;
}

const DEFAULT_KR_UPDATE_CONTEXT: KrUpdateValidationContext = {
  errors: [],
  setErrors: () => undefined,
  clearErrors: () => undefined,
};


export const KrUpdateContext = createContext<KrUpdateValidationContext>(
  DEFAULT_KR_UPDATE_CONTEXT
);

export const KrUpdateProvider = (props: Readonly<KrUpdateProviderProps>) => {
  const [state, setState] = useState<KrUpdateValidationContext>(DEFAULT_KR_UPDATE_CONTEXT);

  const actions = useMemo(
    () => ({
      setErrors: (errors: KrUpdateError[], persist = false) => {
        setState((prev) => {
          const prevErrors: KrUpdateError[] = persist ? prev.errors : []

          return {
            ...prev,
            errors: [
              ...prevErrors,
              ...errors?.filter((err) => err.updateId && err.field && err.errorMsg),
            ],
          }
        });
      },
      clearErrors: (errors: Pick<KrUpdateError, 'field' | 'updateId'>[] | true) => {
        const clearAll = errors === true
        if(clearAll) return setState(DEFAULT_KR_UPDATE_CONTEXT)

        setState((prev) => ({
          ...prev,
          errors: prev.errors.filter((err) => !errors.find(({ field, updateId }) => (
            field === err.field && updateId === err.updateId
          ))),
        }))
      },
    }),
    [setState]
  );

  const value: KrUpdateValidationContext = useMemo(
    () => ({
      ...state,
      ...actions,
    }),
    [state, actions]
  );

  return (
    <KrUpdateContext.Provider value={value}>
      {props.children}
    </KrUpdateContext.Provider>
  );
}

export const useKrUpdateValidation = () => {
  const { errors, setErrors, clearErrors } = useContext(KrUpdateContext)
  
  const getError = useCallback((error: Pick<KrUpdateError, 'field' | 'updateId'>) => {
    return errors.find((err) => err.updateId === error.updateId && err.field === error.field)?.errorMsg
  }, [errors])
  
  return {
    errors,
    setErrors,
    clearErrors,
    getError,
  }
}
